/**
 * API Client
 *
 * Configured Axios instance for communicating with the Mellify backend API.
 *
 * Features:
 * - Base URL configuration from environment variables
 * - Request interceptor: injects JWT access token from auth store
 * - Response interceptor: handles 401 with automatic token refresh & retry
 * - Token refresh queue: concurrent 401s wait for a single refresh call
 * - Timeout and default headers configuration
 */

import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../../stores/authStore';
import { ApiError, getDefaultErrorMessage } from './errors';
import { API_BASE_URL } from '../../config/env';

/**
 * Default request timeout in milliseconds (30 seconds).
 */
export const REQUEST_TIMEOUT = 30_000;

/**
 * Extended timeout for AI generation endpoints in milliseconds (5 minutes).
 * AI content generation, campaign generation, and platform adaptation involve
 * multiple sequential LLM calls, RAG retrieval, competitor research, and large
 * OpenAI generation calls which can take several minutes to complete.
 */
export const AI_REQUEST_TIMEOUT = 300_000;

// ─── Token Refresh State ────────────────────────────────────────────────────

/** Whether a token refresh is currently in progress */
let isRefreshing = false;

/** Queue of requests waiting for the token refresh to complete */
let failedRequestsQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

/**
 * Process the queue of failed requests after a token refresh attempt.
 * If refresh was successful, retry all queued requests with the new token.
 * If refresh failed, reject all queued requests.
 */
function processQueue(error: unknown, token: string | null = null): void {
  failedRequestsQueue.forEach((pending) => {
    if (error) {
      pending.reject(error);
    } else if (token) {
      pending.resolve(token);
    }
  });
  failedRequestsQueue = [];
}

/**
 * Create and configure the Axios instance.
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    withCredentials: true,
  });

  // ─── Request Interceptor: JWT Token Injection ───────────────────────────
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const { tokens } = useAuthStore.getState();

      if (tokens?.accessToken) {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // ─── Response Interceptor: Error Normalization & 401 Token Refresh ─────
  client.interceptors.response.use(
    // Success: pass through
    (response) => response,

    // Error: normalize into ApiError, handle 401 with token refresh
    async (error: AxiosError) => {
      // Network error (no response received)
      if (!error.response) {
        const apiError = new ApiError({
          status: 0,
          message: getDefaultErrorMessage(0),
          code: 'NETWORK_ERROR',
          originalError: error,
        });
        return Promise.reject(apiError);
      }

      const { status, data } = error.response;
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // Extract error details from the response body
      const responseData = data as Record<string, unknown> | undefined;
      const message =
        (responseData?.message as string) ||
        (responseData?.error as string) ||
        getDefaultErrorMessage(status);
      const code = (responseData?.code as string) || undefined;
      const errors = (responseData?.errors as Record<string, string[]>) || undefined;

      // ─── Handle 401: Automatic Token Refresh ───────────────────────────
      if (status === 401 && !originalRequest._retry) {
        const { tokens, isAuthenticated } = useAuthStore.getState();

        // Only attempt refresh if the user is authenticated and has a refresh token
        // and the failed request is NOT the refresh endpoint itself
        const isRefreshRequest = originalRequest.url?.includes('/auth/refresh-token');
        const isLoginRequest = originalRequest.url?.includes('/auth/login');
        const isSignupRequest = originalRequest.url?.includes('/auth/signup');

        if (
          isAuthenticated &&
          tokens?.refreshToken &&
          !isRefreshRequest &&
          !isLoginRequest &&
          !isSignupRequest
        ) {
          // If a refresh is already in progress, queue this request
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedRequestsQueue.push({
                resolve: (newToken: string) => {
                  originalRequest._retry = true;
                  originalRequest.headers.Authorization = `Bearer ${newToken}`;
                  resolve(client(originalRequest));
                },
                reject: (err: unknown) => {
                  reject(err);
                },
              });
            });
          }

          // Start the refresh process
          originalRequest._retry = true;
          isRefreshing = true;

          try {
            // Call the refresh endpoint directly (bypass interceptors to avoid loop)
            const refreshResponse = await axios.post(
              `${API_BASE_URL}/auth/refresh-token`,
              { refreshToken: tokens.refreshToken },
              {
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                },
                timeout: REQUEST_TIMEOUT,
              }
            );

            const responseBody = refreshResponse.data as {
              data?: {
                tokens?: {
                  accessToken?: string;
                  refreshToken?: string;
                };
              };
            };

            const newTokens = responseBody?.data?.tokens;

            if (newTokens?.accessToken) {
              // Update the auth store with new tokens
              useAuthStore.getState().setTokens({
                accessToken: newTokens.accessToken,
                refreshToken: newTokens.refreshToken || null,
              });

              // Process queued requests with new token
              processQueue(null, newTokens.accessToken);

              // Retry the original request with the new token
              originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
              return client(originalRequest);
            } else {
              // Unexpected response format — treat as refresh failure
              throw new Error('Invalid refresh response');
            }
          } catch (refreshError) {
            // Refresh failed — logout and reject all queued requests
            processQueue(refreshError, null);
            useAuthStore.getState().logout();

            const apiError = new ApiError({
              status: 401,
              message: 'Your session has expired. Please sign in again.',
              code: 'SESSION_EXPIRED',
              originalError: refreshError,
            });
            return Promise.reject(apiError);
          } finally {
            isRefreshing = false;
          }
        }

        // No refresh token or not authenticated — just logout if authenticated
        if (isAuthenticated && !isLoginRequest && !isSignupRequest) {
          useAuthStore.getState().logout();
        }
      }

      const apiError = new ApiError({
        status,
        message,
        code,
        errors,
        originalError: error,
      });

      return Promise.reject(apiError);
    }
  );

  return client;
}

/**
 * Singleton Axios API client instance.
 * Use this for all API requests throughout the application.
 *
 * @example
 * ```ts
 * import { apiClient } from '@/services/api/client';
 *
 * const response = await apiClient.get('/users/me');
 * const data = response.data;
 * ```
 */
export const apiClient = createApiClient();

/**
 * Type guard to check if a value looks like the backend API envelope.
 * The backend wraps all responses via ApiResponse.success() in the shape:
 * { success: boolean, data: T, message?: string }
 */
function isApiEnvelope<T>(value: unknown): value is { success: boolean; data: T; message?: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'success' in value &&
    'data' in value &&
    typeof (value as Record<string, unknown>).success === 'boolean'
  );
}

/**
 * Helper to extract data from Axios responses.
 * Automatically unwraps the backend API envelope format:
 *   { success: boolean, data: T, message?: string }
 *
 * If the response body matches the envelope shape, returns the inner `data`.
 * Otherwise, returns the response body as-is for backwards compatibility.
 *
 * Useful for React Query query functions.
 *
 * @example
 * ```ts
 * const fetchUser = () => extractData(apiClient.get<User>('/users/me'));
 * ```
 */
export async function extractData<T>(promise: Promise<{ data: T }>): Promise<T> {
  const response = await promise;
  const body = response.data;

  // Unwrap the API envelope if present
  if (isApiEnvelope<T>(body)) {
    return body.data;
  }

  return body;
}